import React, { useState, useMemo, useEffect, useRef } from "react";
import Select from "react-select";
import OrgChart from "@dabeng/react-orgchart";
import { Container, Row, Col, Card, CardBody, CardHeader, Button } from "reactstrap";
import Breadcrumbs from "components/Common/Breadcrumb";
import Tree from "react-d3-tree";
import ceoImg from "../../../../assets/images/brands/birthday.jpg";
import estelleImg from "../../../../assets/images/brands/birthday.jpg";
import marcelineImg from "../../../../assets/images/brands/birthday2.jpg";
import francoisImg from "../../../../assets/images/brands/birthday3.jpg";
import richardImg from "../../../../assets/images/brands/app-user1.jpg";
import larsImg from "../../../../assets/images/brands/app-user2.jpg";
import sachaImg from "../../../../assets/images/brands/app-user3.jpg";
import takehiroImg from "../../../../assets/images/brands/app-user4.jpg";
import brigitteImg from "../../../../assets/images/brands/user1.jpg";
import Loader from "common/pageLoader";
import { useDispatch, useSelector } from "react-redux";
import { useAuthUserDetails } from "components/Common/getAuthUserDetails";
import { fetchOrgChart } from "store/actions";
import defaultImage from "../../../../assets/images/img_thump.jpg";
import { useLocation,useHistory } from "react-router-dom";
import getAccessRights from "components/Common/getAccessRights";
import useButtonAccess from "components/Common/useButtonAccess";

const containerStyles = {
  width: "100%",
  height: "80vh",
  backgroundColor: "#f9f9f9",
};

// 👥 Hierarchy data
// const orgData = {
//   name: "Chad Gibbons",
//   role: "CEO",
//   image: ceoImg,
//   children: [
//     {
//       name: "Ethan",
//       role: "Manager",
//       image: richardImg,
//       children: [
//         { name: "Isabella Cruz", role: "Employee", image: marcelineImg },
//         { name: "Brigitte Schwartz", role: "Team Lead", image: brigitteImg,
//           children: [
//             { name: "James", role: "Employee", image: takehiroImg },
//             { name: "Carlos", role: "Employee", image: francoisImg },
//           ] 
//         },        
//       ],
//     },
//     {
//       name: "Marceline Anderson",
//       role: "Manager",
//       image: marcelineImg,
//       children: [
//         { name: "Lars Peeters", role: "Employee", image: larsImg},
//       ],
//     },
//     {
//       name: "Francois Mercer",
//       role: "Manager",
//       image: francoisImg,
//       children: [
//         { name: "Sacha Dubois", role: "Employee", image: sachaImg, 
//           children: [
//             { name: "Estelle Darcy", role: "Employee", image: estelleImg }
//           ] 
//         },
//       ],
//     },
//     {
//       name: "Richard Sanchez",
//       role: "Manager",
//       image: takehiroImg,
//       children: [
//         { name: "Takehiro Kanegi", role: "Employee", image: richardImg },
//       ],
//     },
//   ],
// };

// ---------- HELPER FUNCTION ----------
const flattenTree = (node, list = []) => {
  list.push({ name: node.name, role: node.role, image: node.image });
  if (node.children) node.children.forEach((child) => flattenTree(child, list));
  return list;
};

const filterTree = (node, role, name) => {
  // Return all if no filters
  if (!role && !name) return node;

  let matched =
    (!role || node.role === role) && (!name || node.name === name);

  let filteredChildren = [];
  if (node.children) {
    filteredChildren = node.children
      .map((child) => filterTree(child, role, name))
      .filter(Boolean);
  }

  if (matched || filteredChildren.length > 0) {
    return { ...node, children: filteredChildren };
  }
  return null;
};

const CustomNode = ({ nodeDatum }) => (
  <g>
    <foreignObject width="200" height="180" x="-100" y="-90">
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
          fontFamily: "Inter, sans-serif",
          position: "relative",
          overflow: "visible",
        }}
      >
        {/* Profile Image */}
        <img
          src={nodeDatum.image}
          alt={nodeDatum.name}
          style={{
            width: "65px",
            height: "65px",
            borderRadius: "50%",
            border: "3px solid #fff",
            objectFit: "cover",
            boxShadow: "0 0 5px rgba(0,0,0,0.15)",
            backgroundColor: "#fff",
            position: "relative",
            zIndex: 2,
            marginBottom: "-15px", // slightly overlap on the card
          }}
        />

        {/* Card */}
        <div
          style={{
            backgroundColor: "#d1b79e",
            borderRadius: "10px",
            width: "200px",
            textAlign: "center",
            padding: "15px 10px 10px 10px", // adjusted to make space for overlap
            boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
            color: "rgb(57 54 54)",
            position: "relative",
            zIndex: 1,
          }}
        >
          <div
            style={{
              fontSize: "13px",
              fontWeight: "600",
              marginBottom: "3px",
            }}
          >
            {nodeDatum.name}
          </div>
          <div
            style={{
              fontSize: "11px",
              textTransform: "uppercase",
              opacity: 0.95,
            }}
          >
            {nodeDatum.role}
          </div>
        </div>
      </div>
    </foreignObject>
  </g>
);



const OrganizationChart = () => {
  const [accessRight, setAccessRight] = useState(null);
  const location = useLocation();
  const history = useHistory();
  useEffect(() => {
      const rights = getAccessRights(location.pathname, history);
      setAccessRight(rights?.data);
  }, [location]);
  const { canRead, canCreate, canEdit, canDelete } = useButtonAccess(accessRight);
  const dispatch = useDispatch();
  const [authDetails, setAuthDetails] = useState(null);
  const {
    orgChartData,
    loading,
    success,
    error,
  } = useSelector((state) => ({
    orgChartData: state.Dashboard?.orgChartData ?? [],

    loading: state.Dashboard?.loading,
    error: state.Dashboard?.error,
    success: state.Dashboard?.success,
  }));
  const treeContainer = useRef();
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedName, setSelectedName] = useState(null);
  const [orgData, setOrgData] = useState({});
  const [filteredData, setFilteredData] = useState({});
  const [zoom, setZoom] = useState(0.8);
  const [roleOptions, setRoleOptions] = useState([]);
  const [nameOptions, setNameOptions] = useState([]);

  const handleZoomIn = () => setZoom((z) => Math.min(z + 0.2, 2));
  const handleZoomOut = () => setZoom((z) => Math.max(z - 0.2, 0.4));
  const handleResetZoom = () => setZoom(0.8);

  useEffect(() => {
        const authUserDetails = useAuthUserDetails();
        setAuthDetails(authUserDetails[0])
  }, [dispatch]);

  useEffect(()=>{
      if (authDetails) {
        const formData2 = new FormData();
        formData2.append("unit_id", authDetails?.unit_id);
        formData2.append("create_by", authDetails?.user_id);
        dispatch(fetchOrgChart(formData2));
      }
  },[authDetails,dispatch])

  useEffect(() => {
    if (orgChartData && orgChartData.length > 0) {
      // ✅ Normalize and flatten in the same place
      const normalized = normalizeChildren(orgChartData[0]);

      // Flatten nodes recursively
      const flattenNodes = (node) => {
        const children = node.children || [];
        return [node, ...children.flatMap(flattenNodes)];
      };

      const allNodes = flattenNodes(normalized);

      // Build unique role options
      const roles = [...new Set(allNodes.map((n) => n.role))].map((role) => ({
        label: role,
        value: role,
      }));

      // Build name options
      const names = allNodes.map((n) => ({
        label: n.name,
        value: n.name,
      }));

      // Update state safely
      setRoleOptions(roles);
      setNameOptions(names);
      setFilteredData(normalized);
      setOrgData(normalized);
    }
  }, [orgChartData]);

  const normalizeChildren = (node) => {
      if (!node) return null;

      return {
        ...node,
        image: node.image || defaultImage,
        children:
          node.children && Array.isArray(node.children)
            ? node.children.map(normalizeChildren)
            : [], // convert null → []
      };
    };


  const allNodes = useMemo(() => flattenTree(orgData), []);

  // const roleOptions = [
  //   { value: "CEO", label: "CEO" },
  //   { value: "Manager", label: "Manager" },
  //   { value: "Team Lead", label: "Team Lead" },
  //   { value: "Employee", label: "Employee" },
  // ];

  // const nameOptions = [
  //   { value: "Chad Gibbons", label: "Chad Gibbons" },
  //   { value: "Estelle Darcy", label: "Estelle Darcy" },
  //   { value: "Isabella Cruz", label: "Isabella Cruz" },
  //   { value: "Brigitte Schwartz", label: "Brigitte Schwartz" },
  //   { value: "James", label: "James" },
  //   { value: "Carlos", label: "Carlos" },
  //   { value: "Marceline Anderson", label: "Marceline Anderson" },
  //   { value: "Lars Peeters", label: "Lars Peeters" },
  //   { value: "Francois Mercer", label: "Francois Mercer" },
  //   { value: "Sacha Dubois", label: "Sacha Dubois" },
  //   { value: "Ethan", label: "Ethan" },
  //   { value: "Richard Sanchez", label: "Richard Sanchez" },
  //   { value: "Takehiro Kanegi", label: "Takehiro Kanegi" },
  // ];

  // Position the chart in the center
  useEffect(() => {
    if (!treeContainer.current) return;
    const dimensions = treeContainer.current.getBoundingClientRect();

    // Auto center on screen when filtered data changes
    setTranslate({
      x: dimensions.width / 2,
      y: 120,
    });

    // Auto zoom in slightly if filtering is active
    if (selectedName || selectedRole) {
      setZoom(1.1); // zoom in
    } else {
      setZoom(0.8); // default zoom
    }
  }, [filteredData, selectedName, selectedRole]);

  useEffect(() => {
    const applyFilter = () => {
      if (!selectedRole && !selectedName) {
        setFilteredData(orgData);
        return;
      }

      // Find node by name
      const findNode = (node, name) => {
        if (node.name === name) return node;
        for (const child of node.children || []) {
          const found = findNode(child, name);
          if (found) return found;
        }
        return null;
      };

      // Find path (list of ancestors up to the target)
      const findPath = (node, name, path = []) => {
        if (node.name === name) return [...path, node];
        for (const child of node.children || []) {
          const foundPath = findPath(child, name, [...path, node]);
          if (foundPath) return foundPath;
        }
        return null;
      };

      // Build filtered hierarchy preserving correct tree path
      const buildFilteredTree = (root, path) => {
        if (!path || path.length === 0) return null;

        const target = path[path.length - 1];
        const parent = path[path.length - 2];
        const grandParent = path[path.length - 3];
        const topParent = path[path.length - 4];

        // Include siblings at target's level
        let filtered = { ...target };
        if (parent) {
          const siblings = parent.children?.map((c) => ({ ...c })) || [];
          parent.children = siblings;
        }

        // Now rebuild full hierarchy up the chain
        let tree = { ...target };
        if (parent) tree = { ...parent, children: [parent.children.find(c => c.name === target.name) ? parent.children : [target]].flat() };
        if (grandParent) tree = { ...grandParent, children: [tree] };
        if (topParent) tree = { ...topParent, children: [tree] };

        return tree;
      };

      // Filter by role
      const filterByRole = (node, role) => {
        const match = node.role === role;
        const filteredChildren =
          node.children?.map((c) => filterByRole(c, role)).filter(Boolean) || [];
        if (match || filteredChildren.length > 0) {
          return { ...node, children: filteredChildren };
        }
        return null;
      };

      let filtered = null;

      // Case 1: Name-only filter
      if (selectedName && !selectedRole) {
        const path = findPath(orgData, selectedName.value);
        if (path) {
          filtered = buildFilteredTree(orgData, path);
        }
      }

      // Case 2: Role-only filter
      else if (selectedRole && !selectedName) {
        filtered = filterByRole(orgData, selectedRole.value);
      }

      // Case 3: Both filters active
      else if (selectedRole && selectedName) {
        const path = findPath(orgData, selectedName.value);
        if (path) {
          const partial = buildFilteredTree(orgData, path);
          filtered = filterByRole(partial, selectedRole.value);
        }
      }

      setFilteredData(filtered || null);
    };

    applyFilter();
  }, [selectedRole, selectedName]);

  const customPathFunc = (linkData, orientation) => {
      const startX = linkData.source.x;
      const startY = linkData.source.y + 60; // bottom of parent image
      const endX = linkData.target.x;
      const endY = linkData.target.y - 80; // top of child image

      // Draw vertical then horizontal (L shape)
      return `M${startX},${startY} 
              L${startX},${(startY + endY) / 2} 
              L${endX},${(startY + endY) / 2} 
              L${endX},${endY}`;
    };

  return (
    <React.Fragment>
      <div className="page-content">
        <Loader loading={loading}/>
        <Container fluid>
          <Breadcrumbs
            title="Organizational Setup"
            breadcrumbItem="Organization Chart"
          />
           <Row>
            <Col lg="12">
              <Card>
                <CardBody style={{ backgroundColor: "#fff", padding: "20px" }}>
                  {/* 🔹 Filter Section */}
                  <Row className="align-items-center mb-3">
                    <Col md="4" sm="6" xs="12" className="mb-2 mb-md-0">
                      <Select
                        options={roleOptions}
                        placeholder="Select Role"
                        value={selectedRole}
                        onChange={setSelectedRole}
                        isClearable
                      />
                    </Col>

                    <Col md="4" sm="6" xs="12" className="mb-2 mb-md-0">
                      <Select
                        options={nameOptions}
                        placeholder="Select Name"
                        value={selectedName}
                        onChange={setSelectedName}
                        isClearable
                      />
                    </Col>

                    <Col
                      md="4"
                      sm="12"
                      xs="12"
                      className="d-flex justify-content-md-end justify-content-center align-items-center"
                    >
                      <div
                        className="zoom-controls d-flex gap-2 bg-light rounded-pill shadow-sm px-3 py-2"
                        style={{
                          border: "1px solid #e0e0e0",
                          backdropFilter: "blur(6px)",
                          transition: "all 0.3s ease",
                        }}
                      >
                        {/* Zoom Out */}
                        <Button
                          color="primary"
                          size="sm"
                          className="rounded-circle fw-bold shadow-sm"
                          style={{
                            width: "35px",
                            height: "35px",
                            padding: 0,
                            fontSize: "1.2rem",
                            transition: "transform 0.2s ease",
                          }}
                          onClick={handleZoomOut}
                          onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.1)")}
                          onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
                        >
                          −
                        </Button>

                        {/* Reset All */}
                        <Button
                          color="secondary"
                          outline
                          size="sm"
                          className="fw-semibold rounded-pill shadow-sm"
                          style={{
                            padding: "0 15px",
                            fontSize: "0.85rem",
                            transition: "all 0.3s ease",
                          }}
                          onClick={() => {
                            handleResetZoom();     // Reset zoom level
                            setSelectedRole(null); // Clear selected Role
                            setSelectedName(null); // Clear selected Name
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f8f9fa")}
                          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                        >
                          Reset
                        </Button>

                        {/* Zoom In */}
                        <Button
                          color="primary"
                          size="sm"
                          className="rounded-circle fw-bold shadow-sm"
                          style={{
                            width: "35px",
                            height: "35px",
                            padding: 0,
                            fontSize: "1.2rem",
                            transition: "transform 0.2s ease",
                          }}
                          onClick={handleZoomIn}
                          onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.1)")}
                          onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
                        >
                          +
                        </Button>
                      </div>
                    </Col>
                  </Row>

                  {/* 🔹 Tree Container */}
                  <div
                    ref={treeContainer}
                    style={{
                      width: "100%",
                      height: "80vh",
                      border: "1px solid #eee",
                      borderRadius: "8px",
                    }}
                  >
                    {filteredData && (
                      <Tree
                        data={filteredData}
                        translate={translate}
                        orientation="vertical"
                        // pathFunc="elbow"
                        pathFunc={customPathFunc}                        
                        separation={{ siblings: 1.8, nonSiblings: 2.2 }}
                        nodeSize={{ x: 150, y: 200 }}
                        renderCustomNodeElement={CustomNode}
                        zoom={zoom}
                        zoomable
                      />
                    )}
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default OrganizationChart;
